import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Search, ChevronDown } from 'lucide-react';
import Fuse from 'fuse.js';
import { PublicFooter } from '@/components/PublicFooter';
import { JsonLd } from '@/components/seo/JsonLd';
import { buildFAQPageSchema } from '@/components/seo/schemas';
import { usePageMeta } from '@/hooks/usePageMeta';
import { faqData, type FaqItem } from '@/content/faq';

const fuse = new Fuse(faqData, {
  keys: ['question', 'answer', 'category'],
  threshold: 0.4,
  minMatchCharLength: 2,
});

export default function Help() {
  usePageMeta({
    title: 'Help Center & FAQ — Answers for Resellers',
    description:
      'Find answers about For Visibility: connecting eBay, Mercari, and Poshmark, AI listing optimization, pricing, billing, data export, and account support. Search our FAQ or browse by category.',
    canonicalPath: '/help',
    ogPage: 'help',
  });

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const results = useMemo(() => {
    if (!query.trim()) return faqData;
    return fuse.search(query).map((r) => r.item);
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<string, FaqItem[]>();
    for (const item of results) {
      const list = map.get(item.category) ?? [];
      list.push(item);
      map.set(item.category, list);
    }
    return map;
  }, [results]);

  return (
    <div className="min-h-screen bg-fos-bg-base">
      <JsonLd data={buildFAQPageSchema(faqData)} />
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1 text-sm text-fos-text-muted transition-colors hover:text-fos-text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-fos-text-primary">Help Center</h1>
          <p className="mt-2 text-fos-text-muted">Search our FAQ or browse by category</p>
        </div>

        {/* Search */}
        <div className="relative mx-auto mb-8 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fos-text-muted" />
          <input
            type="text"
            placeholder="Search frequently asked questions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg border border-fos-border bg-fos-bg-card py-2.5 pl-10 pr-4 text-sm text-fos-text-primary placeholder-fos-text-muted outline-none ring-fos-accent transition-colors focus:border-fos-accent-border focus:ring-1"
          />
        </div>

        {/* Results count */}
        {query && (
          <p className="mb-4 text-center text-sm text-fos-text-muted">
            {results.length} {results.length === 1 ? 'result' : 'results'} for "{query}"
          </p>
        )}

        {/* FAQ accordion */}
        <div className="space-y-6">
          {Array.from(grouped.entries()).map(([category, items]) => (
            <section key={category}>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-fos-text-muted">
                {category}
              </h2>
              <div className="divide-y divide-fos-border rounded-lg border border-fos-border bg-fos-bg-card">
                {items.map((item) => {
                  const isOpen = expanded === item.question;
                  return (
                    <div key={item.question}>
                      <button
                        onClick={() => setExpanded(isOpen ? null : item.question)}
                        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-fos-text-primary transition-colors hover:bg-fos-bg-hover"
                      >
                        <span>{item.question}</span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-fos-text-muted transition-transform ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="border-t border-fos-border px-4 py-3 text-sm leading-relaxed text-fos-text-secondary">
                          {item.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {results.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-fos-text-muted">No results found for "{query}".</p>
            <p className="mt-1 text-sm text-fos-text-muted">
              Try a different search term or{' '}
              <a
                href="mailto:support@forvisibility.com"
                className="text-fos-accent hover:underline"
              >
                contact support
              </a>
              .
            </p>
          </div>
        )}

        {/* Still need help? */}
        <div className="mt-10 rounded-lg border border-fos-border bg-fos-bg-card p-6 text-center">
          <h2 className="mb-2 font-semibold text-fos-text-primary">Still need help?</h2>
          <p className="mb-4 text-sm text-fos-text-muted">
            Email us and we'll get back to you within 24 hours.
          </p>
          <a
            href="mailto:support@forvisibility.com"
            className="inline-flex items-center gap-2 rounded-lg bg-fos-accent px-4 py-2 text-sm font-medium text-fos-text-inverse transition-opacity hover:opacity-90"
          >
            Contact support
          </a>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
